import * as React from 'react';
import {
  Box,
  Collapse,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
} from '@mui/material';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { useLocation, useNavigate } from 'react-router-dom';
import { items } from '../../router/config';
import { useAuth } from '../../hooks/use-auth';

function NavItemButton({ selected, icon, label, onClick, nested = false }) {
  return (
    <ListItemButton
      selected={selected}
      onClick={onClick}
      sx={{
        py: nested ? 0.85 : 1,
        pl: nested ? 3.5 : 1.5,
        pr: 1.5,
        borderRadius: 2,
        mb: 0.5,
        '&.Mui-selected': {
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            bgcolor: 'primary.dark',
          },
          '& .MuiListItemIcon-root': {
            color: 'primary.contrastText',
          },
        },
      }}
    >
      {icon ? (
        <ListItemIcon sx={{ minWidth: nested ? 28 : 36, color: 'inherit' }}>
          {icon}
        </ListItemIcon>
      ) : (
        <ListItemIcon sx={{ minWidth: 28, color: 'inherit' }}>
          <FiberManualRecordIcon sx={{ fontSize: 8, opacity: selected ? 1 : 0.45 }} />
        </ListItemIcon>
      )}
      <ListItemText
        primary={label}
        primaryTypographyProps={{
          fontSize: nested ? 13 : 14,
          fontWeight: selected ? 600 : 500,
        }}
      />
    </ListItemButton>
  );
}

export default function MenuContent() {
  const auth = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [openSection, setOpenSection] = React.useState(null);

  const toggleSection = (title) => {
    setOpenSection((prev) => (prev === title ? null : title));
  };

  const closeSections = () => {
    setOpenSection(null);
  };

  const isActivePath = (path) => {
    if (!path) {
      return false;
    }
    if (path === '/') {
      return pathname === '/';
    }
    return pathname === path || pathname.startsWith(`${path}/`);
  };

  React.useEffect(() => {
    let activeSection = null;
    items.forEach((item) => {
      if (
        item.children?.some(
          (child) =>
            child.roles?.includes(auth?.user?.role) &&
            (child.path === '/'
              ? pathname === '/'
              : pathname === child.path || pathname.startsWith(`${child.path}/`))
        )
      ) {
        activeSection = item.title;
      }
    });
    setOpenSection(activeSection);
  }, [pathname, auth?.user?.role]);

  return (
    <Stack sx={{ flexGrow: 1, px: 1, py: 1, justifyContent: 'space-between' }}>
      <List dense disablePadding>
        {items.map((item, index) => {
          const canViewParent =
            item.roles?.includes(auth?.user?.role) ||
            item.children?.some((child) => child.roles?.includes(auth?.user?.role));

          if (!canViewParent) {
            return null;
          }

          if (item.children) {
            const visibleChildren = item.children.filter((child) =>
              child.roles?.includes(auth?.user?.role)
            );
            const sectionOpen = openSection === item.title;
            const sectionActive = visibleChildren.some((child) => isActivePath(child.path));

            return (
              <Box key={`${item.title}-${index}`} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleSection(item.title)}
                  sx={{
                    py: 1,
                    px: 1.5,
                    borderRadius: 2,
                    mb: 0.5,
                    bgcolor: sectionActive && !sectionOpen ? 'action.selected' : 'transparent',
                  }}
                >
                  {item.icon && (
                    <ListItemIcon sx={{ minWidth: 36, color: sectionActive ? 'primary.main' : 'text.secondary' }}>
                      {item.icon}
                    </ListItemIcon>
                  )}
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: 600 }}
                  />
                  {sectionOpen ? (
                    <ExpandLessRoundedIcon fontSize="small" color="action" />
                  ) : (
                    <ExpandMoreRoundedIcon fontSize="small" color="action" />
                  )}
                </ListItemButton>
                <Collapse in={sectionOpen} timeout="auto" unmountOnExit>
                  <List dense disablePadding>
                    {visibleChildren.map((child, childIndex) => (
                      <NavItemButton
                        key={`${child.title}-${childIndex}`}
                        nested
                        selected={isActivePath(child.path)}
                        label={child.title}
                        onClick={() => navigate(child.path)}
                      />
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          }

          return (
            <NavItemButton
              key={`${item.title}-${index}`}
              selected={isActivePath(item.path)}
              icon={item.icon}
              label={item.title}
              onClick={() => {
                closeSections();
                navigate(item.path);
              }}
            />
          );
        })}
      </List>
    </Stack>
  );
}
